/**
 * 20名程度のダミー回答から組織レポートデータ(OrgReportData)を生成する（デモ用）。
 *
 * N人分の「個人スコア」を生成し、orgScore.ts の確定済み集約ロジック
 * (aggregateWellbeing / aggregateConnection / aggregateRetention / aggregateOverall)
 * に通して、スコア駆動のフィールド(総合評価・3ゲージ・組織ダイヤモンド・散布図・部署別ヒート)
 * を実際の集約結果から組み立てる。
 *
 * ※ナラティブ(evaluationLead / improvements / closingText 等)はデモ用の簡易テンプレート。
 *   本番の Priority Index / AI文言生成とは別物（実装フェーズで差し替え予定）。
 *
 * 個人スコアの意味は docs/mockups/個人回答_組織スコア集約ロジック設計.md に準拠:
 *  - wellbeing: 1.0〜5.0（Big5 6ファセット平均相当）
 *  - connection 5変数: help/vdep/boss/silo/bridge いずれも「高い=良い」（逆転は適用済み想定）
 *  - retention: 1.0〜5.0（R-01〜03平均、高い=良い）
 *  - axis: ダイヤモンド4軸 A/B/C/D（各0〜12）
 */
import type { AxisKey } from './gb74Report';
import type {
  OrgReportData,
  ScatterDot,
  DeptHeatRow,
  ImprovementItem,
  OrgGauge,
} from './orgReportDummy';
import {
  aggregateWellbeing,
  aggregateConnection,
  aggregateRetention,
  aggregateOverall,
  median,
  toGrade,
  toScore100,
  type ConnectionVars,
  type Grade,
} from './orgScore';
import {
  computeDeptConnectionStructure,
  type DeptInteractionMatrix,
} from './deptConnectionStructure';
import { computeHerrmannLoad, toLoadDisplay } from './herrmannLoad';

export interface DummyOrgOptions {
  /** 回答者数（既定20） */
  respondents?: number;
  /** 会社名 */
  companyName?: string;
  /** ハーマン負荷補正を適用するか (既定 true=デモで見せる) */
  herrmannLoad?: boolean;
  /**
   * ハーマン負荷のデモ用シナリオ (組織ダイヤの組み合わせを差し替えて負荷ケースを見せる)。
   *  - 'balanced'(既定): 多様＋橋渡し(製造がバランス型) → X:維持(据え置き)
   *  - 'polarized': 守る↔変える(B↔D)に二極化＋橋渡し薄 → Yprime:大きく下げ
   *  - 'homogeneous': 同質(A偏重) → 脆さ減点
   */
  scenario?: 'balanced' | 'polarized' | 'homogeneous';
}

interface DeptConfig {
  name: string;
  color: string;
  weight: number;
  /** 軸セントロイド(0〜12) */
  axis: Record<AxisKey, number>;
  /** つながり弱点バイアス（V字・社長集中を低めにする度合い 0〜1） */
  vBias: number;
}

const DEPTS: DeptConfig[] = [
  // 管理部: 堅実(システム×安定)寄り
  { name: '管理部', color: '#8B5CF6', weight: 0.25, axis: { A: 8, B: 9, C: 5, D: 3 }, vBias: 0.5 },
  // 営業部: 推進(人×変化)寄り
  { name: '営業部', color: '#EC4899', weight: 0.3, axis: { A: 4, B: 4, C: 8, D: 9 }, vBias: 0.3 },
  // 製造部: 中央バランス、V字依存がやや強い（デモの最大課題）
  { name: '製造部', color: '#3B82F6', weight: 0.45, axis: { A: 6, B: 7, C: 6, D: 5 }, vBias: 0.7 },
];

const clampAxis = (v: number) => Math.max(0, Math.min(12, Math.round(v)));
const clamp5 = (v: number) => Math.max(1, Math.min(5, v));
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const jitter = (base: number, amp: number) => base + (Math.random() * 2 - 1) * amp;

/**
 * 部署×部署の往来頻度マトリクスを生成する（デモ用）。
 * V字依存バイアス(vBias)が最大の部署をハブ(V字の底)にし、ハブ絡みの往来を強く・
 * 非ハブ同士を弱く設定する。→ ハブの媒介中心性が突出し、三角形が成立しにくい構造になる。
 */
function buildDeptMatrix(): DeptInteractionMatrix {
  const depts = DEPTS.map((d) => d.name);
  const hub = [...DEPTS].sort((a, b) => b.vBias - a.vBias)[0].name;
  const n = depts.length;
  const weight = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const involvesHub = depts[i] === hub || depts[j] === hub;
      // ハブ絡みは強い往来(閾値0.45超)、非ハブ同士は弱い往来(閾値未満)
      weight[i][j] = clamp01(jitter(involvesHub ? 0.75 : 0.3, 0.1));
    }
  }
  return { depts, weight };
}

interface Individual {
  dept: DeptConfig;
  axis: Record<AxisKey, number>;
  wellbeing: number;
  conn: ConnectionVars;
  retention: number;
}

/** 部署配分（重み付き、合計 N 人） */
function allocate(n: number): DeptConfig[] {
  const out: DeptConfig[] = [];
  const counts = DEPTS.map((d) => Math.max(1, Math.round(n * d.weight)));
  // 合計を n に合わせる（端数調整は最大weightの部署で吸収）
  const diff = n - counts.reduce((a, b) => a + b, 0);
  const maxIdx = counts.indexOf(Math.max(...counts));
  counts[maxIdx] += diff;
  DEPTS.forEach((d, i) => {
    for (let k = 0; k < counts[i]; k++) out.push(d);
  });
  return out;
}

function genIndividual(dept: DeptConfig): Individual {
  const axis: Record<AxisKey, number> = {
    A: clampAxis(jitter(dept.axis.A, 2)),
    B: clampAxis(jitter(dept.axis.B, 2)),
    C: clampAxis(jitter(dept.axis.C, 2)),
    D: clampAxis(jitter(dept.axis.D, 2)),
  };
  // つながり5変数: help/bridge は比較的高め、vdep(頼りすぎ)/boss(社長集中) は vBias で低め
  const conn: ConnectionVars = {
    help: clamp5(jitter(3.8, 0.8)),
    bridge: clamp5(jitter(3.5, 0.8)),
    silo: clamp5(jitter(3.3, 0.8)),
    vdep: clamp5(jitter(3.4 - dept.vBias, 0.8)),
    boss: clamp5(jitter(3.4 - dept.vBias * 0.8, 0.8)),
  };
  return {
    dept,
    axis,
    wellbeing: clamp5(jitter(3.5, 0.9)),
    conn,
    retention: clamp5(jitter(3.5, 0.9)),
  };
}

/** 個人軸(0〜12) → 散布図座標。x: 安定(B)←→変化(D), y: システム(A)↑↓人(C) */
function axisToScatter(axis: Record<AxisKey, number>): { x: number; y: number } {
  const x = (axis.D + 0.5) / (axis.B + axis.D + 1); // 高D=右
  const y = (axis.C + 0.5) / (axis.A + axis.C + 1); // 高C=下
  return { x: Math.max(0.04, Math.min(0.96, x)), y: Math.max(0.04, Math.min(0.96, y)) };
}

const GAUGE_KEY_LABEL: Record<'wellbeing' | 'connection' | 'retention', string> = {
  wellbeing: '働きがい',
  connection: 'つながり',
  retention: '離職の兆し',
};

/** デモ用の改善施策テンプレート（弱点カテゴリから1〜3件生成） */
function buildImprovements(
  weakest: 'wellbeing' | 'connection' | 'retention',
  connPerVar: { vdep: number; boss: number }
): ImprovementItem[] {
  const items: ImprovementItem[] = [];
  // つながりが弱点、または V字/社長集中が低いとき
  if (weakest === 'connection' || connPerVar.vdep < 50) {
    items.push({
      rank: items.length + 1,
      rankColor: '#B33A3A',
      title: '特定の人への集中（V字）を「三角形」に変える',
      reason:
        '相談が一部の人に集中しています。仕事のやり方を共有し、3人で相談し合える三角形を増やすと構造的に安定します。',
      action: '頼られている人の仕事のやり方を、メモでいいので1つだけ書き出してもらう',
      effect: 'つながりスコアの底上げが見込めます',
    });
  }
  if (weakest === 'retention') {
    items.push({
      rank: items.length + 1,
      rankColor: '#B8860B',
      title: '若手の「成長実感」を支える場を作る',
      reason:
        '離職の兆しスコアが相対的に低めです。月1回の学びの場で先輩のコツを共有すると定着につながります。',
      action: '入社2〜3年目の社員に「最近、新しく学んだことある？」と聞いてみる',
      effect: '離職の兆しスコアの改善が見込めます',
    });
  }
  if (weakest === 'wellbeing') {
    items.push({
      rank: items.length + 1,
      rankColor: '#B8860B',
      title: '働きがいの土台を整える',
      reason: '働きがいスコアが相対的に低めです。役割と裁量を少しずつ広げると基盤が上がります。',
      action: '承認や判断を1つだけ部下に任せてみる',
      effect: '働きがいスコアの改善が見込めます',
    });
  }
  // 社長集中が低い場合の委譲施策（重複しない範囲で）
  if (connPerVar.boss < 55 && items.length < 3) {
    items.push({
      rank: items.length + 1,
      rankColor: '#999',
      title: '社長・上長への集中を1つ委譲する',
      reason:
        '確認・承認が一部に集中する傾向があります。1つ委譲するだけで三角形が生まれ、自律性が上がります。',
      action: '承認の流れを1つだけ、部下に任せてみる',
      effect: '働きがい・つながりに波及する見込みです',
    });
  }
  // 最低1件は出す
  if (items.length === 0) {
    items.push({
      rank: 1,
      rankColor: '#2D8A4E',
      title: '良い状態を維持する',
      reason: '全体的に良好です。今の助け合いの文化を言語化して共有すると、さらに安定します。',
      action: 'うまくいっている連携の事例を1つ、チームに共有する',
      effect: '現在の良好な状態の維持が見込めます',
    });
  }
  return items.slice(0, 3);
}

/**
 * N人のダミー個人スコアから組織レポートデータを生成する。
 */
export function generateDummyOrgReport(opts: DummyOrgOptions = {}): OrgReportData {
  const respondents = Math.max(7, Math.min(200, opts.respondents ?? 20));
  const companyName = opts.companyName ?? 'デモ株式会社';

  const allocation = allocate(respondents);
  const individuals = allocation.map((d) => genIndividual(d));

  // ---- ハーマン負荷デモ用シナリオ: 個人軸を差し替えて負荷ケースを作る ----
  const scenario = opts.scenario ?? 'balanced';
  const setAxis = (base: Record<AxisKey, number>): Record<AxisKey, number> => ({
    A: clampAxis(jitter(base.A, 1.5)),
    B: clampAxis(jitter(base.B, 1.5)),
    C: clampAxis(jitter(base.C, 1.5)),
    D: clampAxis(jitter(base.D, 1.5)),
  });
  if (scenario === 'polarized') {
    // 半数=守り(B)一点、半数=変化(D)一点 → B↔D分極・橋渡し薄。緩衝も控えめに。
    individuals.forEach((ind, i) => {
      ind.axis = setAxis(i % 2 === 0 ? { A: 2, B: 10, C: 2, D: 2 } : { A: 2, B: 2, C: 2, D: 10 });
      ind.wellbeing = clamp5(jitter(2.9, 0.7));
    });
  } else if (scenario === 'homogeneous') {
    // 全員A偏重 → 脆さ高・橋渡し薄
    individuals.forEach((ind) => {
      ind.axis = setAxis({ A: 10, B: 3, C: 3, D: 2 });
    });
  }

  // ---- スコア集約（実ロジック） ----
  const wellbeingResult = aggregateWellbeing(individuals.map((i) => i.wellbeing));
  const connVars: ConnectionVars = {
    help: median(individuals.map((i) => i.conn.help)),
    vdep: median(individuals.map((i) => i.conn.vdep)),
    boss: median(individuals.map((i) => i.conn.boss)),
    silo: median(individuals.map((i) => i.conn.silo)),
    bridge: median(individuals.map((i) => i.conn.bridge)),
  };
  let connectionResult = aggregateConnection(connVars);
  const retentionResult = aggregateRetention(individuals.map((i) => i.retention));

  // ---- ハーマン・コミュニケーション負荷でつながりスコアを補正 (設計ドラフト・レベル3仮説) ----
  // 緩衝力は個人の働きがい(配慮性/感情制御を含む6ファセット平均)で代理。
  const applyLoad = opts.herrmannLoad !== false;
  const load = computeHerrmannLoad(individuals.map((i) => ({ axis: i.axis, buffer: i.wellbeing })));
  const connBefore = connectionResult.score;
  const loadApplied = applyLoad && load.applicable && load.nudge > 0;
  if (loadApplied) {
    const adj = connectionResult.adjusted * (1 - load.nudge);
    const score = toScore100(adj);
    const { grade, weather } = toGrade(score);
    connectionResult = { ...connectionResult, adjusted: adj, score, grade, weather };
  }

  const overall = aggregateOverall(wellbeingResult, connectionResult, retentionResult);

  // ---- 組織ダイヤモンド（4軸平均、0〜12） ----
  const axis: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  (['A', 'B', 'C', 'D'] as AxisKey[]).forEach((k) => {
    axis[k] =
      Math.round((individuals.reduce((s, i) => s + i.axis[k], 0) / individuals.length) * 10) / 10;
  });
  const dominant = (['A', 'B', 'C', 'D'] as AxisKey[]).reduce((a, b) =>
    axis[a] >= axis[b] ? a : b
  );
  const AXIS_LABEL: Record<AxisKey, string> = { A: 'システム', B: '安定', C: '人', D: '変化' };

  // ---- 散布図（個人ごと） ----
  const scatter: ScatterDot[] = individuals.map((i) => {
    const { x, y } = axisToScatter(i.axis);
    return { dept: i.dept.name, color: i.dept.color, x, y };
  });

  // ---- 部署別ヒート ----
  const deptNames = DEPTS.map((d) => d.name);
  const heatRows: DeptHeatRow[] = deptNames
    .map((name) => {
      const members = individuals.filter((i) => i.dept.name === name);
      if (members.length === 0) return null;
      const wb = aggregateWellbeing(members.map((m) => m.wellbeing));
      const ret = aggregateRetention(members.map((m) => m.retention));
      const helpGrade = toGrade(toScore100(median(members.map((m) => m.conn.help)))).grade;
      const vdepGrade = toGrade(toScore100(median(members.map((m) => m.conn.vdep)))).grade;
      return {
        dept: name,
        cells: [
          { label: '働きがい', grade: wb.grade as Grade },
          { label: '助け合い', grade: helpGrade as Grade },
          { label: '頼りすぎ', grade: vdepGrade as Grade },
          { label: '離職の兆し', grade: ret.grade as Grade },
        ],
      } as DeptHeatRow;
    })
    .filter((r): r is DeptHeatRow => r !== null);

  // ---- ゲージ ----
  const gauges: OrgGauge[] = [
    { label: '働きがい', score: wellbeingResult.score, grade: wellbeingResult.grade },
    {
      label: 'つながり',
      score: connectionResult.score,
      grade: connectionResult.grade,
      emphasis: overall.weakest === 'connection',
    },
    { label: '離職の兆し', score: retentionResult.score, grade: retentionResult.grade },
  ];
  // 弱点ゲージを強調
  const weakLabel = GAUGE_KEY_LABEL[overall.weakest];
  gauges.forEach((g) => {
    if (g.label === weakLabel) g.emphasis = true;
  });

  const target = Math.min(100, overall.score + 11);
  const improvements = buildImprovements(overall.weakest, {
    vdep: connectionResult.perVar.vdep.score,
    boss: connectionResult.perVar.boss.score,
  });

  // ---- §03 つながりの構造 (部署レベルのトリニティ/V字、レベルA) ----
  const connectionStructure = computeDeptConnectionStructure(buildDeptMatrix());

  return {
    companyName,
    meta: `2026年6月実施｜従業員${respondents}名｜デモデータ｜回答率 100%`,
    totalScore: overall.score,
    totalGrade: overall.grade,
    evaluationLead:
      overall.grade === 'A' || overall.grade === 'B'
        ? `全体的に良好な状態です。特に強みを言語化して共有すると、さらに安定します。`
        : `一部に注意が必要な状態です。特に「${weakLabel}」の改善が、全体を良くする最短の道筋です。`,
    axis,
    axisNote: `${AXIS_LABEL[dominant]}の力が組織全体で相対的に高い構成です。`,
    gauges,
    herrmannLoad: applyLoad
      ? toLoadDisplay(load, connBefore, connectionResult.score, loadApplied)
      : undefined,
    distributionLead:
      '部署ごとに思考タイプの傾向が分かれています。管理部は堅実寄り、営業部は推進寄り、製造部は中央のバランス型です。',
    scatter,
    deptLegend: DEPTS.map((d) => ({ dept: d.name, color: d.color })),
    connectionLead: connectionStructure.summary,
    connectionStructure,
    improvementLead: `今の総合評価は${overall.grade}（${overall.score}点）。以下に取り組むと、改善が見込めます。`,
    improvementTarget: target,
    improvements,
    closingText:
      'このレポートはデモ用に生成した20名規模のダミーデータです。\n' +
      'スコア（働きがい・つながり・離職の兆し・総合評価）は orgScore.ts の確定集約ロジックで算出しています。\n\n' +
      'V字を三角形に変えていくこと——\n三角形が1つ増えるだけで、組織はぐっと安定します。',
    heatHeader: ['働きがい', '助け合い', '頼りすぎ', '離職の兆し'],
    heatRows,
  };
}
